import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  tenderService,
  transferWindowService,
  boostPitch,
} from "../../services/api";
import { Search } from "lucide-react";
import { formatCurrencyWithSymbol, formatTransferFee } from "../../utils/currencyFormatter";
import {
  formatSalary as formatSalaryShared,
  getSalaryKind,
  formatRange,
  formatLoanFee,
} from "../../utils/tenderFormatters";
import RequestPreviewModal from "../../components/common/RequestPreviewModal";
import TenderTabs from "../../components/club/TenderTabs";
import CustomDropdown from "../../components/common/CustomDropdown";
import TransferWindowStatusLine from "../../components/common/TransferWindowStatusLine";
import CountryFlag from "../../components/transfer-windows/CountryFlag";
import { useDebounce } from "../../hooks/useDebounce";
import useAnchoredPosition from "../../hooks/useAnchoredPosition";
import StaffTenderCard from "../../components/club/StaffTenderCard";
import { tp_small } from "../../constants/icons";
import PitchDetailsModal from "../../components/agent/PitchDetailsModal";
import ConfirmationModal from "../../components/club/ConfirmationModal";
import { FaFileExcel } from "react-icons/fa";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import AgentTendersMobileHeader from "../../components/agent/responsive/AgentTendersMobileHeader";
import AgentTendersMobileTabs from "../../components/agent/responsive/AgentTendersMobileTabs";
import ListedRequestsListView from "../../components/common/ListedRequestsListView";
import LoanBuySwitch from "../../components/common/LoanBuySwitch";
import { useViewMode } from "../../hooks/useViewMode";
import { newestTenderId } from "../../utils/newestTender";
import { clubInitials } from "../../utils/clubInitials";
import PlayerAvatar from "../../components/common/PlayerAvatar";
import AttachedDocLink from "../../components/common/AttachedDocLink";

/**
 * What the "Transfer fee" column of the agent's own Applied-pitches surface
 * says about a pitch — screen table and Excel export alike.
 *
 * The VERDICT is asked first, the number second. `free_agent` is the resolved
 * server-side answer (dashboard/agent_pitch_logic.resolve_free_agent) and the
 * only one there is; a stored amount answers "how much", never "is there a fee
 * at all". Reading the amount first, as both call sites used to, let a LEGACY
 * row — written before the amount stopped being stored — print a price here
 * while the very same pitch reads "Free Agent" in its details modal
 * (components/agent/PitchDetailsModal.js) and in the club's pitch card.
 *
 * The stored number is left untouched in the database; it is only not shown
 * where the verdict contradicts it.
 *
 * `absent` keeps the surrounding table's own idiom for "nothing here" ("N/A",
 * as every neighbouring column uses). A zero counts as absent, matching
 * `viewpitches/pitchFields.transferFeeOf`: a fee of nothing is not a price, and
 * "€0" states one that was never asked. The old expression tested the raw
 * value, and the backend sends the decimal as the STRING "0.00" — truthy — so
 * a zero-fee row printed exactly that "€0".
 */
const pitchFeeLabel = (pitch, absent = "N/A") => {
  if (pitch?.free_agent === true) return "Free Agent";
  const amount = Number(pitch?.transfer_fee);
  if (!Number.isFinite(amount) || amount <= 0) return absent;
  return formatTransferFee(pitch.transfer_fee, pitch.transfer_fee_currency);
};

const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[400px]">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const PitchPopover = ({
  isOpen,
  onClose,
  pitch,
  buttonRef,
  onViewDetails,
  onBoostPitch,
  onMessageClub,
  isBoostingPitch,
}) => {
  // The menu is a `position: fixed` body portal, so its coordinates are
  // VIEWPORT coordinates. The hand-rolled maths this replaced added
  // window.scrollY/scrollX on top of a getBoundingClientRect() that already
  // accounts for the scroll, pushing the menu down by exactly one scroll
  // offset — on 360 the whole menu landed below the fold and was unreachable
  // (measured on staging: scrollY 323, button bottom 596, menu top 764 in a
  // 640px viewport). The shared anchoring hook (D-003) owns that maths for
  // CustomDropdown and LanguageSelector already and was, per its own header,
  // generalised FROM this popover — so this is the same code path now, and it
  // additionally re-anchors on scroll and resize instead of freezing the
  // position taken at open time. `min-w-[200px]` is kept, the 120px "approximate
  // height" guess is gone: the hook measures the real panel before paint.
  //
  // buttonRef arrives as a fresh `{ current: el }` object on every render of the
  // row; mirroring it into a stable ref keeps the hook's effect from resubscribing
  // its listeners on each render.
  const triggerRef = useRef(null);
  triggerRef.current = buttonRef?.current || null;

  if (!isOpen) return null;

  return createPortal(
    <PitchPopoverPanel
      triggerRef={triggerRef}
      isOpen={isOpen}
      pitch={pitch}
      onClose={onClose}
      onViewDetails={onViewDetails}
      onBoostPitch={onBoostPitch}
      onMessageClub={onMessageClub}
      isBoostingPitch={isBoostingPitch}
    />,
    document.body
  );
};

const PitchPopoverPanel = ({
  triggerRef,
  isOpen,
  pitch,
  onClose,
  onViewDetails,
  onBoostPitch,
  onMessageClub,
  isBoostingPitch,
}) => {
  const { panelRef, style } = useAnchoredPosition(isOpen, triggerRef, {
    align: "right",
    gap: 5,
  });

  return (
    <div
      ref={panelRef}
      data-popover="true"
      className="bg-white py-2 px-2 shadow-lg border rounded-lg z-[9999] min-w-[200px]"
      // Until the hook has measured (first layout pass) the panel is parked
      // off-screen-invisible rather than at 0,0, so there is no origin flash —
      // same contract as CustomDropdown's portal.
      style={style || { position: "fixed", top: 0, left: 0, visibility: "hidden" }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* View pitch details - always show */}
      <div
        onClick={() => {
          onViewDetails(pitch);
          onClose();
        }}
        className="px-2 py-2 text-[#0A0F2D] text-base cursor-pointer hover:bg-gray-50"
      >
        View pitch details
      </div>

      {/* Boost this pitch - only show for active/pitch_sent status and not already boosted */}
      {(pitch.status?.toLowerCase() === "pitch_sent" ||
        pitch.status?.toLowerCase() === "active") &&
        !pitch.is_boosted && (
          <div
            onClick={() => {
              onBoostPitch(pitch);
              onClose();
            }}
            className={`px-2 py-2 text-[#0A0F2D] text-base cursor-pointer hover:bg-gray-50 ${
              isBoostingPitch ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isBoostingPitch ? "Boosting..." : "Boost this pitch"}
          </div>
        )}

      {/* Message club - only show for accepted status */}
      {pitch.status?.toLowerCase() === "accepted" && (
        <div
          onClick={() => {
            onMessageClub(pitch);
            onClose();
          }}
          className="px-2 py-2 text-[#0A0F2D] text-base cursor-pointer hover:bg-gray-50"
        >
          Message club
        </div>
      )}
    </div>
  );
};

const TenderCard = ({ tender, onSaveStatusChange, isNewest = false }) => {
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePitchClick = () => {
    navigate(`/agent/tenders/${tender.id}/pitch`, {
      state: { tenderDetails: tender },
    });
  };

  const handleSaveClick = async () => {
    if (loading) return;
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setLoading(true);
    try {
      let response;
      if (tender.is_saved) {
        response = await tenderService.unsaveTender(tender.id);
      } else {
        response = await tenderService.saveTender(tender.id);
      }

      if (response.success) {
        // Update the tender's saved status in the parent component
        onSaveStatusChange(tender.id, !tender.is_saved);
      }
    } catch (error) {
      console.error("Error saving/unsaving tender:", error);
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  // Format position string (e.g., "attacking_midfielder" -> "Attacking Midfielder")
  const formatPosition = (position) => {
    if (!position) return "";
    return position
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Format currency values
  const formatCurrency = (value) => {
    if (!value) return "";
    return formatCurrencyWithSymbol(value, tender.currency || "USD");
  };

  // Salary + kind come from the shared, null-safe formatter so the card and
  // the loupe preview never diverge (the old local logic dropped any bound
  // that was 0, e.g. net_salary_from=0 / net_salary_to=1000 rendered "-").
  const formatSalary = () => formatSalaryShared(tender) || "-";

  // Get salary type (Net or Gross)
  const getSalaryType = () => getSalaryKind(tender) || "Net";

  // Format market value based on tender type (matches Club TenderCard logic)
  const formatMarketValue = () => {
    if (tender.market_value_type === "not_important") {
      return "Not important";
    }
    if (tender.market_value_type === "market_value_range") {
      return `${formatCurrency(tender.market_value_from)} - ${formatCurrency(
        tender.market_value_to
      )}`;
    } else if (tender.fix_market_value) {
      return formatCurrency(tender.fix_market_value);
    }
    return "-";
  };

  const handleClubClick = () => {
    navigate(`/search/club/${tender.club.wyscout_id}`);
  };

  return (
    <div className="bg-white rounded-lg p-6 border border-[#D2D4D880]">
      {/* Position and Boosted Badge */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-semibold capitalize text-[#0B0B0B]">
              {formatPosition(tender.position)}
            </h3>

            {tender.is_boosted && (
              <span className="flex gap-2 font-medium items-center p-2 bg-gradient-to-r from-[#FF4D4F80] to-[#FAAA0080] text-[#000000] text-[10px] rounded-md capitalize">
                <svg
                  width="6"
                  height="10"
                  viewBox="0 0 6 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.74942 6.25168H0.457751C0.291084 6.25168 0.16782 6.17703 0.0879592 6.02773C0.00809813 5.87842 0.0167787 5.73432 0.114001 5.59543L3.22858 1.11627C3.29803 1.01905 3.38831 0.951337 3.49942 0.913143C3.61053 0.874948 3.72511 0.876684 3.84317 0.918351C3.96122 0.960018 4.04803 1.03293 4.10358 1.1371C4.15914 1.24127 4.17997 1.35238 4.16608 1.47043L3.83275 4.16835H5.44733C5.62789 4.16835 5.75463 4.24821 5.82754 4.40793C5.90046 4.56766 5.87789 4.71696 5.75983 4.85585L2.33275 8.96002C2.25636 9.0503 2.16261 9.10932 2.0515 9.1371C1.94039 9.16488 1.83275 9.15446 1.72858 9.10585C1.62442 9.05724 1.54282 8.98259 1.48379 8.88189C1.42476 8.7812 1.4022 8.67182 1.41608 8.55377L1.74942 6.25168Z"
                    fill="#E11E4A"
                  />
                </svg>
                BOOSTED
              </span>
            )}

            {tender.is_lite && (
              <span className="px-2 py-1 bg-yellow-50 text-yellow-600 text-xs rounded-md capitalize">
                LITE
              </span>
            )}

            {isNewest && (
              <span className="px-2 py-0.5 bg-[#2283FF] text-white text-[10px] font-semibold rounded-full whitespace-nowrap">
                NEWEST
              </span>
            )}
          </div>

          {/* Club Info — a club without a logo gets an initials badge, not a
              broken image. `<img src={undefined}>` renders the browser's broken
              -image glyph next to the alt text, and every card of such a club
              carried one. Same treatment, same shape and size, as
              TenderDetailsModal.js:144-154, which fixed this at its own call
              site; club.logo is null whenever the club has no linked Wyscout
              team, which is the normal state for a self-registered club. */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={handleClubClick}>
            {tender.club?.logo ? (
              <img
                src={tender.club.logo}
                alt={tender.club?.name}
                className="w-8 h-8 object-contain"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div
                className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-[8px] bg-[#E9EEF6] text-[11px] font-bold text-[#6B7280]"
                aria-label={tender.club?.name ? `${tender.club.name} crest` : "Club crest"}
              >
                {clubInitials(tender.club?.name)}
              </div>
            )}
            <span className="text-[18px] text-[#242426] font-medium">
              {tender.club?.name}
            </span>
          </div>
        </div>

        {/* Action icons (top-right): attached-doc paperclip (when present),
            loupe preview, save. NEWEST sits in the top-left badge row, so
            this cluster never collides with it. */}
        <div className="flex items-center gap-2">
          <AttachedDocLink tender={tender} size={5} />
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            aria-label="Preview request"
            title="Quick preview"
          >
            <Search className="w-5 h-5 text-[#535370]" />
          </button>
          <button
            onClick={handleSaveClick}
            disabled={loading}
            className={`p-2 hover:bg-gray-100 rounded-lg ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <svg
              className={`w-5 h-5 ${
                tender.is_saved
                  ? "text-[#2283FF] fill-current"
                  : "text-[#1D1B20]"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex justify-between items-center w-full mb-2">
        <p className="text-[#242426] text-sm font-medium">
          {(() => {
            const country = tender.division_country?.name;
            const division = tender.division;
            const tier = tender.tier;
            const tierLabel =
              tier === 1
                ? "1st Tier"
                : tier === 2
                ? "2nd Tier"
                : tier === 3
                ? "3rd Tier"
                : tier
                ? `${tier}th Tier`
                : null;
            const left = [country, division].filter(Boolean).join(" - ");
            if (left && tierLabel) return `${left} | ${tierLabel}`;
            return left || tierLabel || "-";
          })()}
        </p>
        {(tender.division_country?.name ||
          tender.division_country?.image_url) && (
          <span className="ml-2">
            <CountryFlag
              country={tender.division_country?.name}
              imageUrl={tender.division_country?.image_url}
              width={20}
              height={14}
            />
          </span>
        )}
      </div>

      <p className="text-sm text-[#535370] mb-4 overflow-hidden text-ellipsis line-clamp-2">
        {tender.description || "No description available"}
      </p>

      {/* Market Value & Type */}
      <div className="flex justify-between items-center p-3 bg-[#F7F7F7] rounded-lg mb-4">
        <div className="flex w-full justify-between items-center">
          <span className="text-[#1A212F] text-[14px] font-medium">
            {tender.type_of_tender === "buy"
              ? "Transfer Fee"
              : tender.type_of_tender === "loan"
              ? "Loan Fee"
              : "Market Value"}
          </span>
          <p className="text-[#000000] text-[14px] font-medium">
            {tender.type_of_tender === "buy"
              ? tender.maximum_transfer_fee
                ? formatCurrency(tender.maximum_transfer_fee)
                : "Free transfer"
              : tender.type_of_tender === "loan"
              ? formatLoanFee(tender, tender.currency)
              : formatMarketValue()}
          </p>
        </div>
      </div>

      {/* Essential Details */}
      <div className="flex flex-col gap-3 mb-3">
        <div className="flex justify-between items-center">
          <span className="text-[#242426B2] text-[12px] font-bold">Request Type</span>
          <span
            className={`text-sm px-4 py-1 rounded-full capitalize ${
              tender.type_of_tender === "buy"
                ? "bg-[#049669] text-white"
                : tender.type_of_tender === "loan"
                ? "bg-[#FF9500] text-white"
                : "bg-blue-500 text-white"
            }`}
          >
            {tender.type_of_tender?.toUpperCase()}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[#242426B2] text-[12px] font-bold">
            Games played last season
          </span>
          <p className="text-[12px] text-[#242426] font-medium">
            {tender.matches_played_last_season || "-"}
          </p>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-[#242426B2] text-[12px] font-bold">Age limit</span>
          <p className="text-[12px] text-[#242426] font-medium">
            {/* Shared null-safe formatter: a max-only limit ("Up to 27
                years") now shows on the card too, matching the loupe.
                Old `min_age ? ... : "-"` hid it when only max_age was set. */}
            {formatRange(tender.min_age, tender.max_age, "years") || "-"}
          </p>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[#242426B2] text-[12px] font-bold">Foot</span>
          <p className="text-[12px] text-[#242426] font-medium">
            {tender.foot === "R" ? "Right" : tender.foot === "L" ? "Left" : tender.foot === "A" ? "Any" : "-"}
          </p>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[#242426B2] text-[12px] font-bold">Min. Height</span>
          <p className="text-[12px] text-[#242426] font-medium">
            {tender.minimum_height ? `${tender.minimum_height} cm` : "-"}
          </p>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[#242426B2] text-[12px] font-bold">
            {getSalaryType()} Monthly salary
          </span>
          <p className="text-[12px] text-[#242426] font-medium">{formatSalary()}</p>
        </div>
      </div>

      {/* Key Requirements */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex justify-between items-start gap-2">
          <span className="text-[#242426B2] text-[12px] font-bold whitespace-nowrap">Key Requirements</span>
          <p className="text-[12px] text-[#242426] min-w-0 text-right ml-2 overflow-hidden text-ellipsis line-clamp-2">
            {tender.key_requirements || "-"}
          </p>
        </div>
      </div>

      <button
        onClick={handlePitchClick}
        className="w-full py-2 bg-white border border-[#E6E6E6] text-[#1E1E1E] rounded-lg hover:bg-blue-50 self-end"
      >
        Pitch player
      </button>

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
        title={tender.is_saved ? "Unsave Request" : "Save Request"}
        message={
          tender.is_saved
            ? "Are you sure you want to remove this request from your saved list?"
            : "Are you sure you want to save this request?"
        }
      />

      {/* Read-only quick-preview modal (loupe). Full untruncated tender +
          attached document, mirroring the card's "Pitch player" action. */}
      <RequestPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        tender={tender}
        primaryAction={
          <button
            onClick={() => {
              setShowPreview(false);
              handlePitchClick();
            }}
            className="w-full py-2 bg-white border border-[#E6E6E6] text-[#1E1E1E] rounded-lg hover:bg-blue-50"
          >
            Pitch player
          </button>
        }
      />
    </div>
  );
};

const TenderFilterModal = ({
  isOpen,
  onClose,
  onApply,
  initialFilters,
  selectedType = "player",
  // DECISION 050 follow-up: this page has its own tabs (All / Applied / Saved).
  // Only "saved" maps onto a scope the counts endpoint knows; "all" is its
  // default (the active marketplace) and "applied" has no facet scope yet, so
  // both fall through to the unchanged behaviour.
  activeTab = "",
}) => {
  const [filters, setFilters] = useState(initialFilters);

  // Consolidated filter-option data from /dashboard/tenders/filters/.
  // Each list arrives pre-sorted by tender_count (desc) and contains
  // only options that actually appear on active tenders, so the user
  // never picks a dropdown value that yields zero results.
  const [tenderFilters, setTenderFilters] = useState({
    countries: [],
    divisions: [],
    positions: [],
    continents: [],
  });
  const [filtersLoading, setFiltersLoading] = useState(false);

  // Update filters when initialFilters change (when modal opens)
  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters, isOpen]);

  // Fetch the consolidated filter options. Re-fires when the user
  // changes country or continent so the dependent dropdowns (divisions,
  // positions) narrow accordingly — this is the cascade promised by the
  // backend endpoint.
  useEffect(() => {
    if (!isOpen) return undefined;
    const controller = new AbortController();
    const tender_category =
      selectedType === "players" || selectedType === "player"
        ? "players"
        : "staffs";
    setFiltersLoading(true);
    (async () => {
      try {
        const response = await tenderService.getTenderFilters({
          country: filters.country || "",
          continent: filters.continent || "",
          tender_category,
          // DECISION 050: /agent/tenders is the agent's browse of CLASSIC club
          // requests — the agent-marketplace rows are excluded from that list,
          // so they must be excluded from these counts too. The agent-REQUEST
          // marketplace has its own modal and deliberately sends no surface.
          surface: "classic",
          tab: activeTab === "saved" ? "saved" : "",
          signal: controller.signal,
        });
        if (response.success) {
          setTenderFilters(response.data);
        }
      } catch (error) {
        if (error?.name !== "CanceledError") {
          console.error("Error fetching tender filters:", error);
        }
      } finally {
        setFiltersLoading(false);
      }
    })();
    return () => controller.abort();
  }, [isOpen, filters.country, filters.continent, selectedType, activeTab]);

  // Get positions based on selected type. The caller passes the plural
  // form ("players" / "staffs") that matches the backend's tender_category
  // values, so accept both singular and plural here — historically this
  // check was just `=== "player"` which never matched and silently fell
  // through to the staff/coach list for every caller.
  const getPositions = () => {
    if (selectedType === "player" || selectedType === "players") {
      return [
        { value: "goal_keeper", label: "Goalkeeper" },
        { value: "center_back", label: "Center-Back" },
        { value: "left_back", label: "Left-Back" },
        { value: "right_back", label: "Right-Back" },
        { value: "center_midfielder", label: "Center-Midfielder" },
        { value: "defensive_midfielder", label: "Defensive-Midfielder" },
        { value: "attacking_midfielder", label: "Attacking-Midfielder" },
        { value: "left_winger", label: "Left Winger" },
        { value: "right_winger", label: "Right Winger" },
        { value: "center_forward", label: "Center Forward" },
      ];
    } else {
      return [
        { value: "head_coach", label: "Head Coach" },
        { value: "assistant_coach", label: "Assistant Coach" },
        { value: "goalkeeping_coach", label: "Goalkeeping Coach" },
        {
          value: "strength_and_conditioning_coach",
          label: "Strength and Conditioning Coach",
        },
        { value: "technical_director", label: "Technical Director" },
        { value: "sporting_director", label: "Sporting Director" },
        { value: "team_manager", label: "Team Manager" },
        { value: "scout", label: "Scout" },
        { value: "physiotherapist", label: "Physiotherapist" },
        { value: "sports_doctor", label: "Sports Doctor" },
        { value: "nutritionist", label: "Nutritionist" },
        { value: "sports_psychologist", label: "Sports Psychologist" },
        { value: "performance_analyst", label: "Performance Analyst" },
        { value: "youth_coordinator", label: "Youth Coordinator" },
        { value: "medical_staff", label: "Medical Staff" },
        { value: "youth_coach", label: "Youth Coach" },
        { value: "academy_director", label: "Academy Director" },
        { value: "youth_scout", label: "Youth Scout" },
      ];
    }
  };

  const positions = getPositions();

  const tenderTypeOptions = [
    { value: "all", label: "All Request Types" },
    { value: "buy", label: "Buy" },
    { value: "loan", label: "Loan" },
    { value: "trial", label: "Trial" },
    { value: "full_time", label: "Full Time" },
    { value: "contract", label: "Contract" },
    { value: "temporary", label: "Temporary" },
  ];

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "deal", label: "Deal" },
    { value: "expired", label: "Expired" },
  ];

  const sortOptions = [
    { value: "created_desc", label: "Newest First" },
    { value: "created_asc", label: "Oldest First" },
    { value: "market_value_desc", label: "Highest Market Value" },
    { value: "market_value_asc", label: "Lowest Market Value" },
  ];

  const handleChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleClearAll = () => {
    const defaultFilters = {
      market_value_from: "",
      market_value_to: "",
      division: "",
      position: "",
      nationality: "",
      min_age: "",
      max_age: "",
      status: "all",
      search: "",
      only_eu_player: false,
      only_free_players: false,
      sort_by: "",
      country: "",
      continent: "",
      type_of_tender: "all",
    };
    setFilters(defaultFilters);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-semibold">Filter by</h2>
            <p className="text-[12px] text-[#6B7280] mt-1">
              {filtersLoading
                ? "Loading options…"
                : "Showing only options with active requests"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-secondary hover:text-gray-900"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Market Value Range */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Market Value</h3>
            {(filters.market_value_from || filters.market_value_to) && (
              <button
                onClick={() => {
                  handleChange("market_value_from", "");
                  handleChange("market_value_to", "");
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">From</label>
              <input
                type="number"
                value={filters.market_value_from}
                onChange={(e) =>
                  handleChange("market_value_from", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Min value"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">To</label>
              <input
                type="number"
                value={filters.market_value_to}
                onChange={(e) =>
                  handleChange("market_value_to", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Max value"
              />
            </div>
          </div>
        </div>

        {/* Continent */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Continent</h3>
            {filters.continent && (
              <button
                onClick={() => handleChange("continent", "")}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={tenderFilters.continents.map((c) => ({
              value: c.name,
              label: `${c.name} (${c.tender_count})`,
            }))}
            value={filters.continent}
            onChange={(value) => handleChange("continent", value)}
            placeholder="All Continents"
            displayProperty="label"
            valueProperty="value"
            searchEnabled={true}
            searchPlaceholder="Search continent..."
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Country */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Country</h3>
            {filters.country && (
              <button
                onClick={() => handleChange("country", "")}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={tenderFilters.countries.map((c) => ({
              value: c.name,
              label: `${c.name} (${c.tender_count})`,
              iconClassName: c.code ? `fi fi-${c.code}` : undefined,
              image: c.image_url,
            }))}
            value={filters.country}
            onChange={(value) => handleChange("country", value)}
            placeholder="All Countries"
            displayProperty="label"
            valueProperty="value"
            searchEnabled={true}
            searchPlaceholder="Search country..."
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Division */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Division</h3>
            {filters.division && (
              <button
                onClick={() => handleChange("division", "")}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={tenderFilters.divisions.map((d) => ({
              value: d.name,
              label: d.country
                ? `${d.name} — ${d.country} (${d.tender_count})`
                : `${d.name} (${d.tender_count})`,
            }))}
            value={filters.division}
            onChange={(value) => handleChange("division", value)}
            placeholder="All Divisions"
            displayProperty="label"
            valueProperty="value"
            searchEnabled={true}
            searchPlaceholder="Search division..."
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Position */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Position</h3>
            {filters.position && (
              <button
                onClick={() => handleChange("position", "")}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={(() => {
              // Merge the static position catalog (for selectedType) with
              // the backend's per-position counts. Positions with zero
              // active tenders still appear so the user can pre-set a
              // filter that will become useful as new tenders arrive,
              // but they're sorted to the bottom of the list.
              const countsByValue = Object.fromEntries(
                tenderFilters.positions.map((p) => [p.value, p.tender_count]),
              );
              return positions
                .map((p) => ({
                  value: p.value,
                  label: countsByValue[p.value]
                    ? `${p.label} (${countsByValue[p.value]})`
                    : p.label,
                  _count: countsByValue[p.value] || 0,
                }))
                .sort((a, b) => b._count - a._count);
            })()}
            value={filters.position}
            onChange={(value) => handleChange("position", value)}
            placeholder="All Positions"
            displayProperty="label"
            valueProperty="value"
            searchEnabled={true}
            searchPlaceholder="Search position..."
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Nationality */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Nationality</h3>
            {filters.nationality && (
              <button
                onClick={() => handleChange("nationality", "")}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <input
            type="text"
            value={filters.nationality}
            onChange={(e) => handleChange("nationality", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter nationality"
          />
        </div>

        {/* Age Range */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Age Range</h3>
            {(filters.min_age || filters.max_age) && (
              <button
                onClick={() => {
                  handleChange("min_age", "");
                  handleChange("max_age", "");
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">
                Min Age
              </label>
              <input
                type="number"
                value={filters.min_age || ""}
                onChange={(e) => handleChange("min_age", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Min age"
                min="12"
                max="50"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">
                Max Age
              </label>
              <input
                type="number"
                value={filters.max_age || ""}
                onChange={(e) => handleChange("max_age", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Max age"
                min="12"
                max="50"
              />
            </div>
          </div>
        </div>

        {/* Tender Type */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Request Type</h3>
            {filters.type_of_tender && filters.type_of_tender !== "all" && (
              <button
                onClick={() => handleChange("type_of_tender", "all")}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={tenderTypeOptions}
            value={filters.type_of_tender || "all"}
            onChange={(value) => handleChange("type_of_tender", value)}
            placeholder="All Request Types"
            displayProperty="label"
            valueProperty="value"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Status */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Status</h3>
            {filters.status && filters.status !== "all" && (
              <button
                onClick={() => handleChange("status", "all")}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={statusOptions}
            value={filters.status || "all"}
            onChange={(value) => handleChange("status", value)}
            placeholder="All Status"
            displayProperty="label"
            valueProperty="value"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Player-specific filters */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Player Options</h3>
            {(filters.only_eu_player || filters.only_free_players) && (
              <button
                onClick={() => {
                  handleChange("only_eu_player", false);
                  handleChange("only_free_players", false);
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="space-y-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters.only_eu_player || false}
                onChange={(e) =>
                  handleChange("only_eu_player", e.target.checked)
                }
                className="rounded"
              />
              <span className="text-sm">Only EU Players</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters.only_free_players || false}
                onChange={(e) =>
                  handleChange("only_free_players", e.target.checked)
                }
                className="rounded"
              />
              <span className="text-sm">Only Free Players</span>
            </label>
          </div>
        </div>

        {/* Sort By */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Sort By</h3>
            {filters.sort_by && (
              <button
                onClick={() => handleChange("sort_by", "")}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <CustomDropdown
            options={sortOptions}
            value={filters.sort_by || ""}
            onChange={(value) => handleChange("sort_by", value)}
            placeholder="Default Sorting"
            displayProperty="label"
            valueProperty="value"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={handleClearAll}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
          >
            Clear All
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="bg-gray-100 rounded-full p-4 mb-4">
      <svg
        className="w-8 h-8 text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-1">No pitches yet</h3>
    <p className="text-gray-500 text-center max-w-sm">
      You haven't sent any pitches yet. Start by browsing available Requests and
      pitch your players.
    </p>
  </div>
);

// Using backend country names directly from transfer windows countries API

const AgentTenders = () => {
  const [countries, setCountries] = useState([
    { name: "View All", flag: null, isViewAll: true },
  ]);

  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || "all"
  );
  const [activeSubTab, setActiveSubTab] = useState("tp-tenders");
  const [activePositionFilter, setActivePositionFilter] = useState("players");
  const [sortBy, setSortBy] = useState("pro");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tenders, setTenders] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [savedTenders, setSavedTenders] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [pitchStatus, setPitchStatus] = useState("all");

  // Card ↔ list view toggle for the "All Listed Requests" grid — shared hook
  // (localStorage + URL ?view_mode=) so club and agency behave identically.
  const [viewMode, handleViewModeChange] = useViewMode("requestsViewMode");

  // List-view local action state (preview modal target + in-flight saves).
  const [listPreviewTender, setListPreviewTender] = useState(null);
  const [listSavingIds, setListSavingIds] = useState(new Set());

  // Pitch details modal state
  const [showPitchDetailsModal, setShowPitchDetailsModal] = useState(false);
  const [selectedPitchForDetails, setSelectedPitchForDetails] = useState(null);

  // Boost pitch state
  const [boostingPitches, setBoostingPitches] = useState(new Set());
  const [showBoostConfirmModal, setShowBoostConfirmModal] = useState(false);
  const [selectedPitchForBoost, setSelectedPitchForBoost] = useState(null);

  // Popover state
  const [openPopoverId, setOpenPopoverId] = useState(null);
  const buttonRefs = useRef({});

  // Export state
  const [exportingPitches, setExportingPitches] = useState(false);

  // Transfer Window states
  const [selectedCountry, setSelectedCountry] = useState("Spain");
  const [isTransferWindowDropdownOpen, setIsTransferWindowDropdownOpen] =
    useState(false);
  const [transferWindow, setTransferWindow] = useState(null);
  const [transferWindowLoading, setTransferWindowLoading] = useState(true);
  const [transferWindowSearchQuery, setTransferWindowSearchQuery] =
    useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [pitchPagination, setPitchPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });
  const [savedPagination, setSavedPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });
  const [filters, setFilters] = useState({
    market_value_from: "",
    market_value_to: "",
    division: "",
    position: "",
    nationality: "",
    min_age: "",
    max_age: "",
    status: "all",
    search: "",
    only_eu_player: false,
    only_free_players: false,
    sort_by: "",
    country: "",
    continent: "",
    type_of_tender: "all",
    tender_category: "players",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });

  const navigate = useNavigate();

  // Fetch transfer-window countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const result = await transferWindowService.getTransferWindowCountries(
          "mens_professional"
        );
        if (result.success && Array.isArray(result.data)) {
          const countriesData = [
            { name: "View All", flag: null, isViewAll: true },
            ...result.data.map((c) => ({
              name: c.name || c.value,
              flag: c.image_url,
              isViewAll: false,
            })),
          ];
          setCountries(countriesData);
        }
      } catch (error) {
        console.error("Error fetching transfer-window countries:", error);
      }
    };

    fetchCountries();
  }, []);

  // Update filters when debounced search term changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      search: debouncedSearchTerm,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearchTerm]);

  // Handle click outside for transfer window dropdown and popovers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsTransferWindowDropdownOpen(false);
        setTransferWindowSearchQuery(""); // Reset search when closing
      }

      // Close popover when clicking outside
      if (openPopoverId) {
        // Check if the click is inside the popover or the button that opened it
        const clickedInsidePopover = event.target.closest(
          '[data-popover="true"]'
        );
        const clickedButton = Object.values(buttonRefs.current).some(
          (ref) => ref && ref.contains(event.target)
        );

        if (!clickedInsidePopover && !clickedButton) {
          setOpenPopoverId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openPopoverId]);

  // Filter countries based on search query
  const filteredCountries = countries.filter((country) =>
    country.name.toLowerCase().includes(transferWindowSearchQuery.toLowerCase())
  );

  // Fetch transfer window data
  useEffect(() => {
    const fetchTransferWindow = async () => {
      if (selectedCountry === "View All") return;

      setTransferWindowLoading(true);
      try {
        const response = await transferWindowService.getCurrentWindow(
          selectedCountry,
          "mens_professional"
        );

        if (response.success && response.data) {
          setTransferWindow(response.data);
        }
      } catch (error) {
        console.error("Error fetching transfer window:", error);
      } finally {
        setTransferWindowLoading(false);
      }
    };

    fetchTransferWindow();
  }, [selectedCountry]);

  // Reset filters when changing tabs
  useEffect(() => {
    const defaultFilters = {
      market_value_from: "",
      market_value_to: "",
      division: "",
      position: "",
      nationality: "",
      min_age: "",
      max_age: "",
      status: "all",
      search: "",
      only_eu_player: false,
      only_free_players: false,
      sort_by: "",
      country: "",
      continent: "",
      type_of_tender: "all",
      tender_category: "players",
    };
    setFilters(defaultFilters);
    setSearchTerm(""); // Also reset the search term input
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [activeTab]);

  const formatDateRange = (windowData) => {
    if (!windowData) return "";

    // Handle new API response format
    if (windowData.current_start && windowData.current_end) {
      const startDate = new Date(windowData.current_start);
      const endDate = new Date(windowData.current_end);

      const formatDate = (date) => {
        return date.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      };

      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }

    // Handle old format for backward compatibility
    const now = new Date();
    const currentTime = now.getTime();

    const window1Start = new Date(windowData.window_1_start);
    const window1End = new Date(windowData.window_1_end);
    const window2Start = new Date(windowData.window_2_start);
    const window2End = new Date(windowData.window_2_end);

    let startDate, endDate;

    if (
      currentTime >= window1Start.getTime() &&
      currentTime <= window1End.getTime()
    ) {
      startDate = window1Start;
      endDate = window1End;
    } else if (
      currentTime >= window2Start.getTime() &&
      currentTime <= window2End.getTime()
    ) {
      startDate = window2Start;
      endDate = window2End;
    } else if (currentTime < window1Start.getTime()) {
      startDate = window1Start;
      endDate = window1End;
    } else {
      startDate = window2Start;
      endDate = window2End;
    }

    const formatDate = (date) => {
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const handleTransferWindowDropdownToggle = () => {
    setIsTransferWindowDropdownOpen(!isTransferWindowDropdownOpen);
    if (!isTransferWindowDropdownOpen) {
      // Focus search input when opening dropdown
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleCountrySelect = (country) => {
    if (country.isViewAll) {
      navigate("/transfer-windows");
    } else {
      setSelectedCountry(country.name);
    }
    setIsTransferWindowDropdownOpen(false);
    setTransferWindowSearchQuery(""); // Reset search after selection
  };

  const fetchTenders = useCallback(
    async (page = 1) => {
      if (activeTab !== "all") return;

      setLoading(true);
      setError(null);

      const controller = new AbortController();

      try {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("view", "all");
        params.append(
          "is_lite",
          activeSubTab === "tp-lite-tenders" ? "true" : "false"
        );

        // Add filters if they exist
        Object.entries(filters).forEach(([key, value]) => {
          if (key === "sort_by" && value) {
            // Handle sort_by separately as it's already handled below
            return;
          }
          if (typeof value === "boolean" && value === true) {
            // For boolean filters, only add if true
            params.append(key, "true");
          } else if (
            value &&
            value !== "all" &&
            value !== "" &&
            value !== false
          ) {
            // For other filters, add if they have meaningful values
            params.append(key, value);
          }
        });

        // Add tender category based on activePositionFilter
        if (activePositionFilter) {
          params.append("tender_category", activePositionFilter);
        }

        // Add recruiting_for based on sortBy state
        const sortValue = filters.sort_by || sortBy;
        if (sortValue === "pro") {
          params.append("recruiting_for", "professional");
        } else if (sortValue === "youth") {
          params.append("recruiting_for", "youth");
        }

        // Add sort if it exists (for actual sorting like newest first, etc.)
        if (sortValue) {
          params.append("sort_by", sortValue);
        }

        // Add search query if exists
        if (searchQuery) params.append("search", searchQuery);

        const response = await tenderService.getAllTenders(
          params,
          controller.signal
        );

        if (response.success) {
          setTenders(response.data);
          setPagination(response.pagination);
        } else {
          setError(response.error);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to fetch requests. Please try again.");
      } finally {
        setLoading(false);
      }

      return () => controller.abort();
    },
    [
      activeTab,
      filters,
      searchQuery,
      activeSubTab,
      activePositionFilter,
      sortBy,
    ]
  );

  // Fetch agent pitches
  const fetchPitches = useCallback(
    async (page = 1) => {
      if (activeTab !== "applied") return;

      setLoading(true);
      setError(null);

      const controller = new AbortController();

      try {
        const params = new URLSearchParams();
        params.append("page", page);

        // Add status filter if not 'all'
        if (pitchStatus !== "all") {
          params.append("status", pitchStatus);
        }

        // Add search query if exists
        if (searchQuery) {
          params.append("search", searchQuery);
        }

        const response = await tenderService.getAgentPitches(
          params,
          controller.signal
        );

        if (response.success) {
          setPitches(response.data);
          setPitchPagination({
            count: response.pagination.count,
            next: response.pagination.next,
            previous: response.pagination.previous,
            currentPage: page,
          });
        } else {
          setError(response.error);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to fetch pitches. Please try again.");
      } finally {
        setLoading(false);
      }

      return () => controller.abort();
    },
    [activeTab, pitchStatus, searchQuery]
  );

  // Fetch saved tenders
  const fetchSavedTenders = useCallback(
    async (page = 1) => {
      if (activeTab !== "saved") return;

      setLoading(true);
      setError(null);

      const controller = new AbortController();

      try {
        const params = new URLSearchParams();
        params.append("page", page);

        // Add search query if exists
        if (searchQuery) {
          params.append("search", searchQuery);
        }

        const response = await tenderService.getSavedTenders(
          params,
          controller.signal
        );

        if (response.success) {
          setSavedTenders(response.data);
          setSavedPagination({
            count: response.pagination.count,
            next: response.pagination.next,
            previous: response.pagination.previous,
            currentPage: page,
          });
        } else {
          setError(response.error);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to fetch saved requests. Please try again.");
      } finally {
        setLoading(false);
      }

      return () => controller.abort();
    },
    [activeTab, searchQuery]
  );

  // Reset filters when changing tabs
  useEffect(() => {
    setFilters({
      type: "player",
      market_value_from: "",
      market_value_to: "",
      division: "",
      position: "",
      nationality: "",
      min_age: "",
      max_age: "",
      status: "all",
      only_eu_player: false,
      only_free_players: false,
      sort_by: "",
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [activeTab]);

  // Reset states when changing tabs
  useEffect(() => {
    if (activeTab === "applied") {
      setPitchStatus("all");
      setSearchQuery("");
      setPitchPagination((prev) => ({ ...prev, currentPage: 1 }));
    } else if (activeTab === "saved") {
      setSearchQuery("");
      setSavedPagination((prev) => ({ ...prev, currentPage: 1 }));
    } else {
      // ... existing reset logic for other tabs ...
    }
  }, [activeTab]);

  // Fetch data when dependencies change
  useEffect(() => {
    const abortController = new AbortController();

    if (activeTab === "all") {
      fetchTenders(pagination.currentPage);
    } else if (activeTab === "applied") {
      fetchPitches(pitchPagination.currentPage);
    } else if (activeTab === "saved") {
      fetchSavedTenders(savedPagination.currentPage);
    }

    return () => {
      abortController.abort();
    };
  }, [
    activeTab,
    fetchTenders,
    fetchPitches,
    fetchSavedTenders,
    pagination.currentPage,
    pitchPagination.currentPage,
    savedPagination.currentPage,
  ]);

  const handleFilterChange = (newFilters) => {
    // Reset pagination when applying new filters
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setFilters(newFilters);
    // Also update search term to sync with filters
    setSearchTerm(newFilters.search || "");
  };

  const clearAllFilters = () => {
    const defaultFilters = {
      market_value_from: "",
      market_value_to: "",
      division: "",
      position: "",
      nationality: "",
      min_age: "",
      max_age: "",
      status: "all",
      search: "",
      only_eu_player: false,
      only_free_players: false,
      sort_by: "",
      country: "",
      continent: "",
      type_of_tender: "all",
      tender_category: "players",
    };
    setFilters(defaultFilters);
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    const activeFilterCount = [
      filters.market_value_from,
      filters.market_value_to,
      filters.division,
      filters.position,
      filters.nationality,
      filters.min_age,
      filters.max_age,
      filters.status !== "all",
      filters.search,
      filters.only_eu_player,
      filters.only_free_players,
      filters.sort_by,
      filters.country,
      filters.continent,
      filters.type_of_tender !== "all",
      // Note: tender_category is not included in active filter count as it should always be set
    ].filter(Boolean).length;

    return activeFilterCount;
  };

  const handlePositionFilterChange = (filterId) => {
    // Reset pagination when changing position filter
    setPagination((prev) => ({ ...prev, currentPage: 1 }));

    // Ensure we never clear to "all" - always default to "players"
    const safeFilterId = filterId || "players";
    setActivePositionFilter(safeFilterId);
  };

  const handleSortChange = (value) => {
    // Reset pagination when changing sort
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSortBy(value || "pro"); // Ensure it never becomes empty, default to pro
  };

  const handleSearchChange = (value) => {
    // Update search term immediately for UI responsiveness
    setSearchTerm(value);
  };

  const tabs = [
    { id: "all", label: "All Listed Requests" },
    { id: "applied", label: "Applied pitches" },
    { id: "saved", label: "Saved" },
  ];

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Handle boost pitch confirmation
  const handleBoostPitchClick = (pitch) => {
    setSelectedPitchForBoost(pitch);
    setShowBoostConfirmModal(true);
    setOpenPopoverId(null); // Close the popover
  };

  // Handle actual boost pitch after confirmation
  const handleBoostPitchConfirm = async () => {
    if (!selectedPitchForBoost || boostingPitches.has(selectedPitchForBoost.id))
      return;

    const pitchId = selectedPitchForBoost.id;
    setShowBoostConfirmModal(false);
    setBoostingPitches((prev) => new Set([...prev, pitchId]));

    try {
      const response = await boostPitch(pitchId);

      if (response.success) {
        toast.success("Pitch boosted successfully!");

        // Update the pitch in the list to reflect boosted status
        setPitches((prevPitches) =>
          prevPitches.map((pitch) =>
            pitch.id === pitchId ? { ...pitch, is_boosted: true } : pitch
          )
        );
      } else {
        toast.error(response.error || "Failed to boost pitch");
      }
    } catch (error) {
      console.error("Error boosting pitch:", error);
      toast.error("Failed to boost pitch. Please try again.");
    } finally {
      setBoostingPitches((prev) => {
        const newSet = new Set(prev);
        newSet.delete(pitchId);
        return newSet;
      });
      setSelectedPitchForBoost(null);
    }
  };

  // Handle message club
  const handleMessageClub = (pitch) => {
    const channelId = `pitch-${pitch.id}`;
    const channelType = "messaging";

    console.log("channelId", channelId);
    console.log("channelType", channelType);

    // return
    navigate("/inbox", {
      state: {
        channelId: channelId,
        channelType: channelType,
      },
    });
  };

  // Handle view pitch details
  const handleViewPitchDetails = (pitch) => {
    setSelectedPitchForDetails(pitch);
    setShowPitchDetailsModal(true);
  };

  // ── List-view bridge handlers ───────────────────────────────────────
  // The list view reuses the same actions as the card view so behaviour
  // stays identical regardless of which layout the user picks.
  const handleListPitch = (tender) => {
    navigate(`/agent/tenders/${tender.id}/pitch`, {
      state: { tenderDetails: tender },
    });
  };

  const handleListPreview = (tender) => {
    setListPreviewTender(tender);
  };

  const handleListToggleSave = async (tender) => {
    if (listSavingIds.has(tender.id)) return;
    setListSavingIds((prev) => new Set([...prev, tender.id]));
    try {
      const response = tender.is_saved
        ? await tenderService.unsaveTender(tender.id)
        : await tenderService.saveTender(tender.id);
      if (response.success) {
        handleSaveStatusChange(tender.id, !tender.is_saved);
      }
    } catch (error) {
      console.error("Error saving/unsaving tender:", error);
    } finally {
      setListSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(tender.id);
        return next;
      });
    }
  };

  // Add function to handle save status changes
  const handleSaveStatusChange = (tenderId, isSaved) => {
    // Update tenders list
    if (activeTab === "all") {
      setTenders((prevTenders) =>
        prevTenders.map((tender) =>
          tender.id === tenderId ? { ...tender, is_saved: isSaved } : tender
        )
      );
    }

    // Update saved tenders list
    if (activeTab === "saved" && !isSaved) {
      setSavedTenders((prevTenders) =>
        prevTenders.filter((tender) => tender.id !== tenderId)
      );
    }
  };

  // Export applied pitches to Excel
  const handleExportAppliedPitches = async () => {
    if (activeTab !== "applied") {
      toast.error("Export is only available for applied pitches");
      return;
    }

    if (pitches.length === 0) {
      toast.error("No applied pitches data available to export");
      return;
    }

    setExportingPitches(true);
    try {
      // Prepare data for Excel export based on what's shown in the table
      const exportData = pitches.map((pitch) => ({
        "Club Name": pitch?.created_by?.name || pitch?.club?.name || "N/A",
        "Pitched Player": pitch.player?.name || pitch.player_name || "N/A",
        Position: pitch.position || pitch.player?.position || "N/A",
        "Transfer Fee": pitchFeeLabel(pitch),
        Status:
          pitch.status?.charAt(0).toUpperCase() + pitch.status?.slice(1) ||
          "N/A",
        "Date Sent": pitch.created_at
          ? new Date(pitch.created_at).toLocaleDateString()
          : pitch.date_sent
          ? new Date(pitch.date_sent).toLocaleDateString()
          : "N/A",
        "Player Image URL": pitch.player?.image || pitch.player_image || "N/A",
        "Club Logo URL": pitch?.club?.logo || "N/A",
        // No fee, no currency: a code beside a "Free Agent" fee column would
        // be a second, contradicting statement about the same row.
        "Transfer Fee Currency":
          pitch?.free_agent === true
            ? "N/A"
            : pitch?.transfer_fee_currency || "EUR",
        "Is Boosted": pitch.is_boosted ? "Yes" : "No",
        "Pitch ID": pitch.id || "N/A",
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns
      const colWidths = [];
      const headers = Object.keys(exportData[0]);
      headers.forEach((header, i) => {
        const maxWidth = Math.max(
          header.length,
          ...exportData.map((row) => String(row[header] || "").length)
        );
        colWidths[i] = { wch: Math.min(maxWidth + 2, 50) };
      });
      ws["!cols"] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Applied Pitches");

      // Generate filename with current date
      const fileName = `Applied_Pitches_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      // Save file
      XLSX.writeFile(wb, fileName);

      toast.success(
        `Successfully exported ${exportData.length} applied pitch records to Excel!`
      );
    } catch (error) {
      console.error("Error exporting applied pitches to Excel:", error);
      toast.error("Failed to export data to Excel. Please try again.");
    } finally {
      setExportingPitches(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 ">
      {/* Header Section */}
      {isMobile ? (
        <AgentTendersMobileHeader
          activeTab={activeTab}
          transferWindowLoading={transferWindowLoading}
          transferWindow={transferWindow}
          formatDateRange={formatDateRange}
          dropdownRef={dropdownRef}
          searchInputRef={searchInputRef}
          searchQuery={transferWindowSearchQuery}
          setSearchQuery={setTransferWindowSearchQuery}
          handleTransferWindowDropdownToggle={
            handleTransferWindowDropdownToggle
          }
          countries={countries}
          selectedCountry={selectedCountry}
          isTransferWindowDropdownOpen={isTransferWindowDropdownOpen}
          filteredCountries={filteredCountries}
          handleCountrySelect={handleCountrySelect}
        />
      ) : (
        <div className="border-b bg-white ">
          <div className=" mx-auto px-12">
            <div className="flex justify-between items-center py-4">
              <h1 className="text-xl font-semibold">Requests</h1>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <TransferWindowStatusLine
                    country={selectedCountry}
                    category="mens_professional"
                    variant="precise-dates"
                    className="text-[#000000] text-sm font-medium"
                  />
                </div>
                <div className="relative" ref={dropdownRef}>
                  <button
                    className="flex items-center gap-2 px-3 py-1.5 border rounded-lg hover:bg-gray-50"
                    onClick={handleTransferWindowDropdownToggle}
                  >
                    <CountryFlag
                      country={selectedCountry}
                      imageUrl={
                        countries.find((c) => c.name === selectedCountry)?.flag
                      }
                      width={24}
                      height={16}
                    />
                    <span>{selectedCountry}</span>
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${
                        isTransferWindowDropdownOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {isTransferWindowDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[250px] overflow-hidden">
                      {/* Search Input */}
                      <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                              className="h-4 w-4 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                          </div>
                          <input
                            ref={searchInputRef}
                            type="text"
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm text-gray-900"
                            placeholder="Search country"
                            value={transferWindowSearchQuery}
                            onChange={(e) =>
                              setTransferWindowSearchQuery(e.target.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>

                      {/* Countries List */}
                      <div className="overflow-y-auto max-h-48">
                        {filteredCountries.length > 0 ? (
                          filteredCountries
                            .filter((country) => !country.isViewAll)
                            .map((country, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 text-gray-700"
                                onClick={() => handleCountrySelect(country)}
                              >
                                <CountryFlag
                                  country={country.name}
                                  imageUrl={country.flag}
                                  width={20}
                                  height={14}
                                />
                                <span className="text-sm">{country.name}</span>
                              </div>
                            ))
                        ) : (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            No countries found
                          </div>
                        )}
                      </div>

                      {/* View All Button */}
                      <div
                        className="px-3 py-2 cursor-pointer bg-[#2283FF] text-center text-[#F5F5F5]"
                        onClick={() => handleCountrySelect({ isViewAll: true })}
                      >
                        <span className="text-sm font-medium">View All</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs Section */}
      <div className=" mx-auto  ">
        {isMobile ? (
          <AgentTendersMobileTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onFilterClick={() => setShowFilterModal(true)}
            sortBy={sortBy}
            handleSortChange={handleSortChange}
            searchTerm={searchTerm}
            handleSearchChange={handleSearchChange}
            onSubTabChange={setActiveSubTab}
            activeSubTab={activeSubTab}
            activePositionFilter={activePositionFilter}
            handlePositionFilterChange={handlePositionFilterChange}
            pitchStatus={pitchStatus}
            onPitchStatusChange={setPitchStatus}
            onExport={handleExportAppliedPitches}
            exporting={exportingPitches}
            exportDisabled={pitches.length === 0}
            typeOfTender={filters.type_of_tender}
            onTypeOfTenderChange={(value) =>
              handleFilterChange({ ...filters, type_of_tender: value })
            }
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
          />
        ) : (
          <>
            {/* One-line filter bar (agent-only file): nowrap so the tabs (left)
                and the control group (right) stay distributed on a single line
                via justify-between — never wrap/bunch. Keeps the bottom border
                separator. The left tabs group may shrink/scroll; the control
                group keeps its size. */}
            <div className="flex flex-nowrap justify-between items-center gap-x-4 bg-white px-4 md:px-12 py-4 border-b border-[#E5E6E8] overflow-x-auto overflow-y-hidden">
              <div className="flex flex-nowrap items-center gap-2 md:gap-6 min-w-0">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-lg text-base whitespace-nowrap ${
                      activeTab === tab.id
                        ? "bg-[#F3F4F6] text-[#0B0B0B]"
                        : "text-[#1E1E1ECC] hover:bg-gray-50"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {/* Control group: nowrap, never shrinks — stays on the one line. */}
              <div className="flex flex-nowrap items-center gap-3 flex-shrink-0">
                {activeTab === "applied" ? (
                  <>
                    <div className="relative min-w-0 flex-shrink w-[120px] sm:w-[150px] lg:w-[200px]">
                      <input
                        type="text"
                        placeholder="Search by position or club name"
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-[#E6E6E6] rounded-lg placeholder:text-[#1E1E1E]"
                      />
                      <svg
                        className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        fill="none"
                        stroke="#2C2C2C"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <div className="min-w-[180px]">
                      <CustomDropdown
                        options={[
                          { value: "all", label: "All Status" },
                          { value: "pitch_sent", label: "Pitch Sent" },
                          { value: "accepted", label: "Accepted" },
                          { value: "rejected", label: "Rejected" },
                        ]}
                        value={pitchStatus}
                        onChange={(value) => setPitchStatus(value)}
                        // Dead prop, aligned rather than made visible.
                        // CustomDropdown shows the placeholder only while
                        // `value` is falsy; `pitchStatus` starts at "all" and
                        // "all" is an option, so the trigger always reads
                        // "All Status" and "Filter by Status" could never
                        // appear. "All Status" is the WANTED label — it names
                        // the current state instead of an instruction, the
                        // mobile twin (AgentTendersMobileTabs.js) passes the
                        // same string, and the FAQ walk clicks the control by
                        // that exact text. So the contradiction is removed
                        // from the code, not from the screen.
                        placeholder="All Status"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-[#2283FF] rounded-lg"
                      />
                    </div>
                    <button
                      onClick={handleExportAppliedPitches}
                      disabled={exportingPitches || pitches.length === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-[#2283FF] text-white text-sm rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {exportingPitches ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Exporting...
                        </>
                      ) : (
                        <>
                          <FaFileExcel className="w-4 h-4" />
                          Export to Excel
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="relative min-w-0 flex-shrink w-[120px] sm:w-[150px] lg:w-[200px]">
                      <input
                        type="text"
                        placeholder="Search by position or club name"
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-[#E6E6E6] rounded-lg placeholder:text-[#1E1E1E]"
                      />
                      <svg
                        className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        fill="none"
                        stroke="#2C2C2C"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <button
                      onClick={() => setShowFilterModal(true)}
                      className={`relative flex-shrink-0 flex items-center gap-2 px-4 py-[6px] border rounded-lg hover:bg-gray-50 ${
                        hasActiveFilters() > 0
                          ? "border-blue-500 text-blue-600 bg-blue-50"
                          : "border-[#E6E6E6]"
                      }`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                        />
                      </svg>
                      Filters
                      {/* Count badge is absolutely positioned so its
                          appearance (on Buy/Loan) adds NO layout width — the
                          filter row no longer jumps when toggled. */}
                      {hasActiveFilters() > 0 && (
                        <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] leading-none bg-blue-500 text-white rounded-full">
                          {hasActiveFilters()}
                        </span>
                      )}
                    </button>
                    {/* Clear All as a compact, fixed-size icon button. Always
                        occupies the same slot (just hidden when inactive) so
                        the row width is identical whether or not a filter is
                        active — toggling All/Buy/Loan never wraps the row. */}
                    <button
                      onClick={clearAllFilters}
                      aria-label="Clear all filters"
                      title="Clear all filters"
                      className={`flex-shrink-0 p-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 ${
                        hasActiveFilters() > 0
                          ? ""
                          : "invisible pointer-events-none"
                      }`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>

                    {/* Loan / Buy segmented control — shared with the club
                        view (see common/LoanBuySwitch). Drives the same
                        `type_of_tender` filter the modal uses, staying in sync
                        with the modal's Request Type dropdown.

                        NOT on "All Listed Requests": there the request TYPE is
                        a filter INSIDE the chosen SOURCE, so it may not sit
                        above the TP / TP Lite tabs — it renders on its own row
                        directly under them instead (see below). Saved has no
                        source tabs, so there the switch keeps this slot and
                        that tab stays pixel-identical. */}
                    {activeTab !== "all" && (
                      <div className="flex-shrink-0">
                        <LoanBuySwitch
                          value={filters.type_of_tender}
                          onChange={(value) =>
                            handleFilterChange({
                              ...filters,
                              type_of_tender: value,
                            })
                          }
                        />
                      </div>
                    )}

                    {/* Cards/List toggle moved to the category-tab row
                        (see <TenderTabs> below) to declutter this filter row. */}

                    <div className="min-w-[110px] flex-shrink-0">
                      <CustomDropdown
                        options={[
                          { value: "pro", label: "Pro" },
                          { value: "youth", label: "Youth" },
                        ]}
                        value={sortBy}
                        onChange={(value) => handleSortChange(value)}
                        placeholder="Pro"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-[#2283FF] rounded-lg"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Sub Tabs */}
            {activeTab === "all" && (
              <>
                <TenderTabs
                  activeTab={activeSubTab}
                  onTabChange={setActiveSubTab}
                  isBgWhite={true}
                  isBorder={false}
                  isFromAgent={true}
                  onPositionFilterChange={handlePositionFilterChange}
                  activePositionFilter={activePositionFilter}
                  viewMode={viewMode}
                  onViewModeChange={handleViewModeChange}
                />
                {/* Buy / Loan on its own row, directly UNDER the source tabs —
                    parent choice (TP / TP Lite) above, child filter below. The
                    phone bar already does exactly this
                    (agent/responsive/AgentTendersMobileTabs.js:249-256); the
                    desktop kept the switch up in the filter toolbar, i.e. above
                    the tabs it filters within.
                    `-mt-6` swallows TenderTabs' own `mb-6` so the switch sits
                    in the same white block as the tabs; the `mb-6` here keeps
                    the unchanged gap to the cards below. */}
                <div className="-mt-6 mb-6 flex items-center bg-white px-4 md:px-12 pb-4">
                  <LoanBuySwitch
                    value={filters.type_of_tender}
                    onChange={(value) =>
                      handleFilterChange({
                        ...filters,
                        type_of_tender: value,
                      })
                    }
                  />
                </div>
              </>
            )}
          </>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Error State */}
        {error && <div className="text-red-500 text-center py-8">{error}</div>}

        {/* Content */}
        {activeTab === "all"
          ? !loading &&
            !error && (
              <>
                {tenders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No Requests found
                  </div>
                ) : viewMode === "list" ? (
                  <ListedRequestsListView
                    tenders={tenders}
                    onPreview={handleListPreview}
                    onToggleSave={handleListToggleSave}
                    savingIds={listSavingIds}
                    newestId={newestTenderId(tenders)}
                    // Clean list layout (kept) + the card's primary CTA:
                    // the agent card's action is "Pitch player", reusing the
                    // same navigation handler (handleListPitch).
                    renderActions={(tender) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleListPitch(tender);
                        }}
                        className="px-4 py-2 bg-[#2283FF] text-white font-medium rounded-lg hover:bg-blue-600"
                      >
                        Pitch player
                      </button>
                    )}
                  />
                ) : (
                  <div
                    className={`grid mt-4 md:mt-0 grid-cols-1 md:grid-cols-3 gap-6 ${
                      isMobile ? "px-4" : "px-12"
                    }`}
                  >
                    {(() => {
                      const newestId = newestTenderId(tenders);
                      return tenders.map((tender) =>
                        tender.tender_category === "staff" ? (
                          <StaffTenderCard
                            key={tender.id}
                            tender={tender}
                            onSaveStatusChange={handleSaveStatusChange}
                            isNewest={tender.id === newestId}
                          />
                        ) : (
                          <TenderCard
                            key={tender.id}
                            tender={tender}
                            onSaveStatusChange={handleSaveStatusChange}
                            isNewest={tender.id === newestId}
                          />
                        )
                      );
                    })()}
                  </div>
                )}

                {/* Pagination */}
                {pagination.count > 10 && (
                  <div className="flex justify-center items-center gap-2 mt-8">
                    <button
                      onClick={() => fetchTenders(pagination.currentPage - 1)}
                      disabled={!pagination.previous}
                      className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2">
                      Page {pagination.currentPage} of{" "}
                      {Math.ceil(pagination.count / 10)}
                    </span>
                    <button
                      onClick={() => fetchTenders(pagination.currentPage + 1)}
                      disabled={!pagination.next}
                      className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )
          : activeTab === "applied"
          ? !loading &&
            !error && (
              <>
                {pitches.length === 0 ? (
                  <EmptyState />
                ) : (
                  <div className="mx-4 md:mx-12 my-4">
                    <div className="bg-white rounded-lg border border-[#D6DBEA] overflow-x-auto">
                      <table className="min-w-full table-sticky-first">
                        <thead className="bg-[#F7F7F7]">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs sm:text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                              TO CLUB
                            </th>
                            <th className="px-6 py-3 text-left text-xs sm:text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                              PITCHED PLAYER
                            </th>
                            <th className="px-6 py-3 text-left text-xs sm:text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                              POSITION
                            </th>
                            <th className="px-6 py-3 text-left text-xs sm:text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                              TRANSFER FEE
                            </th>
                            <th className="px-6 py-3 text-left text-xs sm:text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                              STATUS
                            </th>
                            <th className="px-6 py-3 text-left text-xs sm:text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                              DATE SENT
                            </th>
                            <th className="px-6 py-3 text-left text-xs sm:text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                              ACTION
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#D6DBEA]">
                          {pitches.map((pitch) => (
                            <tr
                              key={pitch.id}
                              className="bg-white hover:bg-gray-50"
                            >
                              {/* TO CLUB */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div
                                  onClick={() => {
                                    if (pitch?.club?.wyscout_id) {
                                      navigate(
                                        `/search/club/${pitch.club.wyscout_id}`
                                      );
                                    }
                                  }}
                                  className="cursor-pointer flex items-center gap-2 flex-col sm:flex-row"
                                >
                                  {pitch?.club?.logo ? (
                                    <img
                                      src={pitch?.club?.logo}
                                      alt={pitch?.club?.name}
                                      className="w-7 h-7 object-contain"
                                    />
                                  ) : (
                                    <div className="w-7 h-7 bg-[#2283FF] rounded-full flex items-center justify-center">
                                      <span className="text-white text-xs font-bold">
                                        {pitch?.club?.name
                                          ?.charAt(0)
                                          ?.toUpperCase() || "C"}
                                      </span>
                                    </div>
                                  )}
                                  {/* The column is TO CLUB, and everything else
                                      in this cell already speaks about the club:
                                      the logo is `pitch.club.logo`, the click
                                      navigates to `pitch.club.wyscout_id`. The
                                      NAME read `pitch.created_by.name`, which on
                                      an agent's own applied-pitches table is the
                                      agent himself — so every row of "to which
                                      club did I pitch" answered with the
                                      reader's own name. */}
                                  <span className="text-[#222A35] text-xs sm:text-sm font-bold truncate">
                                    {pitch?.club?.name || "Club Name"}
                                  </span>
                                </div>
                              </td>

                              {/* PITCHED PLAYER */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div
                                  onClick={() => {
                                    if (pitch?.player?.wyscout_id) {
                                      navigate(
                                        `/search/player/${pitch.player.wyscout_id}`
                                      );
                                    }
                                  }}
                                  className="cursor-pointer flex items-center gap-2"
                                >
                                  <div className="relative">
                                    {/* A player without a photo used to make
                                        this an <img> with NO src at all —
                                        neither field is set — so every row of
                                        such a player showed the browser's
                                        broken-image glyph. PlayerAvatar is the
                                        app's canonical person fallback
                                        (common/PlayerAvatar.jsx:3-8) and also
                                        catches a photo URL that 404s, which a
                                        plain null-check cannot. */}
                                    <PlayerAvatar
                                      src={
                                        pitch.player?.image ||
                                        pitch.player_image
                                      }
                                      name={
                                        pitch.player?.name || pitch.player_name
                                      }
                                      className="w-8 h-8 rounded-lg text-[11px]"
                                    />
                                    {/* TP Badge */}
                                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#2283FF] rounded-full flex items-center justify-center">
                                      <img
                                        src={tp_small}
                                        alt="TP"
                                        className="w-full h-full"
                                      />
                                    </div>
                                  </div>
                                  <span
                                    title={
                                      pitch.player?.name || pitch.player_name
                                    }
                                    className="text-[#222A35] text-xs sm:text-sm font-medium truncate max-w-[180px] block"
                                  >
                                    {pitch.player?.name || pitch.player_name}
                                  </span>
                                </div>
                              </td>

                              {/* POSITION */}
                              <td className="px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-normal truncate">
                                {pitch.position ||
                                  pitch.player?.position ||
                                  "N/A"}
                              </td>

                              {/* TRANSFER FEE — the verdict, then the number.
                                  See pitchFeeLabel at the top of this file. */}
                              <td className="px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-normal truncate">
                                {pitchFeeLabel(pitch)}
                              </td>

                              {/* STATUS */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-1.5 py-1 rounded text-xs font-medium whitespace-nowrap ${
                                    pitch.status?.toLowerCase() === "pitch sent"
                                      ? "bg-[#F7F0E6] text-[#BE6F01]"
                                      : pitch.status?.toLowerCase() ===
                                        "accepted"
                                      ? "bg-[#EBF9EE] text-[#049669]"
                                      : pitch.status?.toLowerCase() ===
                                        "rejected"
                                      ? "bg-red-100 text-red-600"
                                      : "bg-gray-100 text-gray-600"
                                  }`}
                                >
                                  {pitch.status?.charAt(0).toUpperCase() +
                                    pitch.status?.slice(1) || "N/A"}
                                </span>
                              </td>

                              {/* DATE SENT */}
                              <td className="px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-normal">
                                {formatDate(
                                  pitch.created_at || pitch.date_sent
                                )}
                              </td>

                              {/* ACTION */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="relative flex gap-1">
                                  {pitch?.status?.toLowerCase() ===
                                    "accepted" && (
                                    <div
                                      onClick={() => handleMessageClub(pitch)}
                                      className="bg-white border cursor-pointer border-[#E6E6E6] p-1.5 flex items-center justify-center rounded-md"
                                    >
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 14 13"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M12.728 6.21258C12.7302 7.05249 12.5339 7.88105 12.1553 8.63076C11.7063 9.52914 11.016 10.2848 10.1618 10.813C9.30762 11.3413 8.32323 11.6213 7.31889 11.6217C6.47898 11.6239 5.65042 11.4276 4.90071 11.0489L1.27344 12.258L2.48253 8.63076C2.10385 7.88105 1.90761 7.05249 1.9098 6.21258C1.91019 5.20824 2.19019 4.22384 2.71844 3.36965C3.24669 2.51546 4.00233 1.82521 4.90071 1.37621C5.65042 0.997532 6.47898 0.801295 7.31889 0.803485H7.63707C8.96348 0.876662 10.2163 1.43651 11.1556 2.37585C12.095 3.31518 12.6548 4.56799 12.728 5.89439V6.21258Z"
                                          stroke="#2C2C2C"
                                          strokeWidth="1.27273"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </div>
                                  )}

                                  <button
                                    ref={(el) => {
                                      if (el) {
                                        buttonRefs.current[pitch.id] = el;
                                      }
                                    }}
                                    className="p-1.5 bg-white border border-[#E6E6E6] rounded-md"
                                    onClick={() => {
                                      setOpenPopoverId(
                                        openPopoverId === pitch.id
                                          ? null
                                          : pitch.id
                                      );
                                    }}
                                  >
                                    <svg
                                      className="w-4 h-4 text-[#2C2C2C] rotate-90"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                                      />
                                    </svg>
                                  </button>

                                  <PitchPopover
                                    isOpen={openPopoverId === pitch.id}
                                    onClose={() => setOpenPopoverId(null)}
                                    pitch={pitch}
                                    buttonRef={
                                      buttonRefs.current[pitch.id]
                                        ? {
                                            current:
                                              buttonRefs.current[pitch.id],
                                          }
                                        : { current: null }
                                    }
                                    onViewDetails={handleViewPitchDetails}
                                    onBoostPitch={handleBoostPitchClick}
                                    onMessageClub={handleMessageClub}
                                    isBoostingPitch={boostingPitches.has(
                                      pitch.id
                                    )}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Pagination */}
                {pitchPagination.count > 10 && (
                  <div className="flex justify-center items-center gap-2 mt-8">
                    <button
                      onClick={() =>
                        fetchPitches(pitchPagination.currentPage - 1)
                      }
                      disabled={!pitchPagination.previous}
                      className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2">
                      Page {pitchPagination.currentPage} of{" "}
                      {Math.ceil(pitchPagination.count / 10)}
                    </span>
                    <button
                      onClick={() =>
                        fetchPitches(pitchPagination.currentPage + 1)
                      }
                      disabled={!pitchPagination.next}
                      className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )
          : // Saved tenders view
            !loading &&
            !error && (
              <>
                {savedTenders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No saved Requests found
                  </div>
                ) : (
                  <div
                    className={`grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 ${
                      isMobile ? "px-4" : "px-12"
                    }`}
                  >
                    {savedTenders.map((tender) =>
                      tender.tender_category === "staff" ? (
                        <StaffTenderCard
                          key={tender.id}
                          tender={tender}
                          onSaveStatusChange={handleSaveStatusChange}
                        />
                      ) : (
                        <TenderCard
                          key={tender.id}
                          tender={tender}
                          onSaveStatusChange={handleSaveStatusChange}
                        />
                      )
                    )}
                  </div>
                )}

                {/* Pagination */}
                {savedPagination.count > 10 && (
                  <div className="flex justify-center items-center gap-2 mt-8">
                    <button
                      onClick={() =>
                        fetchSavedTenders(savedPagination.currentPage - 1)
                      }
                      disabled={!savedPagination.previous}
                      className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2">
                      Page {savedPagination.currentPage} of{" "}
                      {Math.ceil(savedPagination.count / 10)}
                    </span>
                    <button
                      onClick={() =>
                        fetchSavedTenders(savedPagination.currentPage + 1)
                      }
                      disabled={!savedPagination.next}
                      className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
      </div>

      {/* Filter Modal */}
      <TenderFilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApply={handleFilterChange}
        initialFilters={filters}
        activeTab={activeTab}
        // Drive the position list off the visible sub-tab (activePositionFilter),
        // not filters.tender_category — the two can drift out of sync (the sub-
        // tab toggle updates activePositionFilter only), and using
        // tender_category here previously made the modal show staff/coach
        // positions while the sub-tab clearly said Players.
        selectedType={activePositionFilter}
      />

      {/* List-view quick-preview modal (loupe). Mirrors the card's preview;
          the primary action reuses the same pitch navigation. */}
      <RequestPreviewModal
        isOpen={!!listPreviewTender}
        onClose={() => setListPreviewTender(null)}
        tender={listPreviewTender}
        primaryAction={
          listPreviewTender ? (
            <button
              onClick={() => {
                const t = listPreviewTender;
                setListPreviewTender(null);
                handleListPitch(t);
              }}
              className="w-full py-2 bg-white border border-[#E6E6E6] text-[#1E1E1E] rounded-lg hover:bg-blue-50"
            >
              Pitch player
            </button>
          ) : null
        }
      />

      {/* Pitch Details Modal */}
      <PitchDetailsModal
        isOpen={showPitchDetailsModal}
        onClose={() => {
          setShowPitchDetailsModal(false);
          setSelectedPitchForDetails(null);
        }}
        pitch={selectedPitchForDetails}
      />

      {/* Boost Pitch Confirmation Modal */}
      <ConfirmationModal
        isOpen={showBoostConfirmModal}
        onClose={() => {
          setShowBoostConfirmModal(false);
          setSelectedPitchForBoost(null);
        }}
        onConfirm={handleBoostPitchConfirm}
        variant="v2"
        title="Boost Pitch"
        message={`Are you sure you want to boost the pitch for "${
          selectedPitchForBoost?.player?.name || "this player"
        }"? This will cost 400 Transfer Points.`}
        confirmText="Boost Pitch"
        confirmButtonClass="bg-[#2283FF] hover:bg-blue-600"
      />
    </div>
  );
};

export default AgentTenders;
